import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { DateFormat } from '../../../constants/date-format.constants';
import { IFiltersObject } from '../../../models/common';
import {
    BaseFilter,
    BaseListFilter,
    BaseRangeFilter,
    DateFilter,
    DateRangeFilterRequest,
    DecimalFilter,
    EnumFilter,
    FilterRequest,
    IntegerFilter,
    StringFilter
} from '@lsq-frontend/remote-services/enterprise/common';
import { FilterHelper } from '../../../utilities/filter-helper';
import { ITableColumn } from '../../models/table-column';
import { FilterType } from '../filters/constants/filter-types';

@Component({
    selector: 'lsq-filter-box',
    template: `
        <div *ngIf="filterableColumns.length > 0" class="lsq-filter-box-container">
            <p>
                <span class="filter-title">Filters:</span>
                <span
                    class="filter-no-filters"
                    *ngIf="!filterHelper.areAnyFiltersSet(filterableColumns, request)"
                    >No filters are currently set</span
                >
                <span *ngFor="let column of filterableColumns; let i = index">
                    <span *ngIf="column.filterField">
                        <span
                            *ngIf="
                                filterHelper.getFilterType(column.type) === filterType.Text &&
                                getStringFilter(column)
                            "
                            class="filter-option"
                            (click)="clearFilter(column, newStringFilter)"
                        >
                            <span>
                                (
                                <span class="text-bold">{{ column.header }}:</span>
                                <span class="filter-list-options">{{
                                    getStringFilter(column)
                                }}</span>
                                )
                                <span class="close-icon">
                                    <i class="far fa-times"></i>
                                </span>
                            </span>
                        </span>

                        <span
                            *ngIf="
                                filterHelper.getFilterType(column.type) === filterType.Float &&
                                getNumberStringFilter(column)
                            "
                            class="filter-option"
                            (click)="clearFilter(column, newDecimalFilter)"
                        >
                            <span>
                                (
                                <span class="text-bold">{{ column.header }}:</span>
                                <span class="filter-list-options">{{
                                    getNumberStringFilter(column)
                                }}</span>
                                )
                                <span class="close-icon">
                                    <i class="far fa-times"></i>
                                </span>
                            </span>
                        </span>

                        <span
                            *ngIf="
                                filterHelper.getFilterType(column.type) === filterType.Int &&
                                getNumberStringFilter(column)
                            "
                            class="filter-option"
                            (click)="clearFilter(column, newIntegerFilter)"
                        >
                            <span>
                                (
                                <span class="text-bold">{{ column.header }}:</span>
                                <span class="filter-list-options">{{
                                    getNumberStringFilter(column)
                                }}</span>
                                )
                                <span class="close-icon">
                                    <i class="far fa-times"></i>
                                </span>
                            </span>
                        </span>

                        <span
                            *ngIf="
                                filterHelper.getFilterType(column.type) === filterType.Enum &&
                                getEnumStringFilter(column)
                            "
                            class="filter-option"
                            (click)="clearFilter(column, newEnumFilter)"
                        >
                            <span>
                                (
                                <span class="text-bold">{{ column.header }}:</span>
                                <span class="filter-list-options">
                                    {{ getEnumStringFilter(column) }}</span
                                >
                                )
                                <span class="close-icon">
                                    <i class="far fa-times"></i>
                                </span>
                            </span>
                        </span>

                        <span
                            *ngIf="
                                filterHelper.getFilterType(column.type) === filterType.Date &&
                                getDateStringFilter(column)
                            "
                            class="filter-option"
                            (click)="clearFilter(column, newDateFilter)"
                        >
                            <span>
                                (
                                <span class="text-bold">{{ column.header }}:</span>
                                <span class="filter-list-options">
                                    {{ getDateStringFilter(column) }}</span
                                >
                                )
                                <span class="close-icon">
                                    <i class="far fa-times"></i>
                                </span>
                            </span>
                        </span>
                    </span>
                </span>
            </p>
        </div>
    `,
    styleUrls: ['./lsq-filter-box.component.scss']
})
export class LsqFilterBox implements OnInit {
    @Input() public columns: ITableColumn[];
    @Input() public request?: FilterRequest | DateRangeFilterRequest;
    @Output() public filterUpdated = new EventEmitter<IFiltersObject>();
    public filterType = FilterType;
    public filterableColumns: ITableColumn[];
    private noFilterText = '';

    public newDateFilter = new DateFilter();
    public newStringFilter: StringFilter = null;
    public newDecimalFilter = new DecimalFilter();
    public newIntegerFilter = new IntegerFilter();
    public newEnumFilter = new EnumFilter<number>();
    public filterHelper = FilterHelper;

    constructor(private datePipe: DatePipe) {}

    public ngOnInit(): void {
        this.filterableColumns = this.getFilterableColumns();
    }

    public getFilterableColumns(): ITableColumn[] {
        const filterableColumns: ITableColumn[] = [];
        this.columns.forEach((column) => {
            if (column.filterField) {
                filterableColumns.push(column);
            }
        });
        return filterableColumns;
    }

    public getDateStringFilter(column: ITableColumn): string {
        const dateFilter: DateFilter = this.request[column.filterField];
        let filterString = this.noFilterText;
        if (dateFilter != null) {
            if (dateFilter.toValue && !dateFilter.fromValue) {
                filterString = `Before ${this.datePipe.transform(
                    dateFilter.toValue,
                    DateFormat.ShortMonthAndYear
                )}`;
            }

            if (!dateFilter.toValue && dateFilter.fromValue) {
                filterString = `After ${this.datePipe.transform(
                    dateFilter.fromValue,
                    DateFormat.ShortMonthAndYear
                )}`;
            }

            if (dateFilter.toValue && dateFilter.fromValue) {
                filterString = `Between ${this.datePipe.transform(
                    dateFilter.fromValue,
                    DateFormat.ShortMonthAndYear
                )} and ${this.datePipe.transform(
                    dateFilter.toValue,
                    DateFormat.ShortMonthAndYear
                )}`;
            }
        }
        return filterString;
    }

    public getStringFilter(column: ITableColumn): string {
        const stringFilter: StringFilter = this.request[column.filterField];
        return stringFilter && stringFilter.value
            ? `Contains ${stringFilter.value}`
            : this.noFilterText;
    }

    public getEnumStringFilter(column: ITableColumn): string {
        let filterString = this.noFilterText;
        const enumFilter: EnumFilter<number> = this.request[column.filterField];

        if (enumFilter && enumFilter.values && enumFilter.values.length) {
            filterString = '';
            enumFilter.values.forEach((value, index) => {
                filterString += column.enum[value];
                if (index + 1 !== enumFilter.values.length) {
                    filterString += ', ';
                }
            });
        }

        return filterString;
    }

    public getNumberStringFilter(column: ITableColumn): string {
        let filterString = this.noFilterText;
        const numberFilter = this.request[column.filterField];
        if (numberFilter) {
            if (numberFilter.toValue !== null && numberFilter.fromValue !== null) {
                if (numberFilter.toValue === numberFilter.fromValue) {
                    filterString = `Equals ${numberFilter.toValue}`;
                } else {
                    filterString = `Between ${numberFilter.fromValue} & ${numberFilter.toValue}`;
                }
            } else if (numberFilter.toValue !== null && numberFilter.fromValue === null) {
                filterString = `Less than or equal to ${numberFilter.toValue}`;
            } else if (numberFilter.toValue === null && numberFilter.fromValue !== null) {
                filterString = `Greater than or equal to ${numberFilter.fromValue}`;
            }
        }

        return filterString;
    }

    public clearFilter(
        column: ITableColumn,
        newFilter: BaseFilter<any> | BaseListFilter<any> | BaseRangeFilter<any>
    ): void {
        this.filterUpdated.emit({ [column.filterField]: newFilter });
    }
}
